<template>
  <div class="reward-affiliates-page">
    <KTCodePreview v-bind:title="'Danh sách chương trình hoa hồng Affiliate'">
      <template v-slot:toolbar>
        <div
          class="row"
          @click="openCreatePage()"
          v-if="checkPermission('REWARD_AFFILIATE_INSERT')"
        >
          <b-button size="sm" variant="primary">
            <i style="font-size: 1rem" class="flaticon2-add-1"></i>
            <b>Thêm mới</b>
          </b-button>
        </div>
      </template>
      <template v-slot:preview>
        <!-- Filter section -->
        <b-row class="mb-5">
          <b-col md="3">
            <div class="search-col">
              <date-picker
                placeholder="Từ ngày"
                class="form-control-sm"
                :config="dpConfigs.date"
                v-model="params.startAt"
              ></date-picker>
              <span class="ml-1 mr-1"></span>
              <date-picker
                placeholder="Đến ngày"
                class="form-control-sm"
                :config="dpConfigs.date"
                v-model="params.endAt"
              ></date-picker>
            </div>
          </b-col>
          <b-col md="3">
            <b-form-input
              v-model.trim="params.keyword"
              type="text"
              placeholder="Tìm kiếm theo mã hoặc tên chương trình"
              size="sm"
              v-on:keyup.enter="onFilter()"
            ></b-form-input>
          </b-col>
          <b-col md="3">
            <b-form-select
              size="sm"
              v-model="params.status"
              :options="statusOptions"
              value-field="value"
              text-field="text"
              class="select-style"
            >
              <template v-slot:first>
                <b-form-select-option :value="null"
                  >-- Chọn trạng thái --</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-col>
          <b-col md="2">
            <b-button
              variant="primary"
              size="sm"
              class="btn font-weight-bold btn-size"
              @click="onFilter"
              :disabled="onLoading"
              >Tìm kiếm</b-button
            >
          </b-col>
        </b-row>

        <b-table
          :items="listRewardAffiliates"
          :fields="fields"
          bordered
          hover
          :busy="onLoading"
          class="reward-table"
        >
          <template v-slot:table-busy>
            <vcl-table :speed="5" :animate="true" :columns="10"></vcl-table>
          </template>

          <template v-slot:cell(period)="row">
            <div class="font-weight-bold">
              {{ formatDate(row.item.startAt, DATE_FORMAT) }} -
              {{ formatDate(row.item.endAt, DATE_FORMAT) }}
            </div>
          </template>

          <template v-slot:cell(status)="row">
            <span
              class="label font-weight-bold label-lg label-inline mb-2"
              :class="getStatusClass(row.item.status)"
            >
              {{ getStatusText(row.item.status) }}
            </span>
          </template>

          <template v-slot:cell(createdBy)="row">
            <div class="font-weight-bold">
              {{ row.item.createdByEmployee?.fullName }}
            </div>
          </template>

          <template #cell(actions)="data">
            <b-dropdown size="sm" no-caret right lazy>
              <template slot="button-content">
                <i style="font-size: 1rem" class="flaticon2-settings pr-0"></i>
              </template>
              <b-dropdown-item
                v-if="checkPermission('REWARD_AFFILIATE_UPDATE')"
                @click="openEditPage(data.item)"
              >
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="flaticon2-pen"></i>
                  &nbsp; Chỉnh sửa
                </span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="checkPermission('REWARD_AFFILIATE_VIEW')"
                @click="openViewDetailPage(data.item)"
              >
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="flaticon-eye"></i>
                  &nbsp; Chi tiết
                </span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="checkPermission('REWARD_AFFILIATE_INSERT')"
                @click="openClonePage(data.item)"
              >
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="flaticon2-copy"></i>
                  &nbsp; Nhân bản
                </span>
              </b-dropdown-item>
              <b-dropdown-item
                @click="
                  showDeleteAlert(
                    'Xoá chương trình hoa hồng!',
                    `Bạn có chắc muốn chương trình '${data.item.name}'. Lưu ý thao tác này không thể hoàn tác!`,
                    data.item,
                    confirmDeleteRewardAffiliate,
                  )
                "
                v-if="checkPermission('REWARD_AFFILIATE_DELETE')"
              >
                <span style="color: #3f4254; font-size: 12px">
                  <i
                    style="font-size: 1rem; color: #d33"
                    class="flaticon2-rubbish-bin-delete-button"
                  ></i>
                  &nbsp; Xóa
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>

        <b-row>
          <b-col>
            <p class="mt-3 text-dark font-weight-bold">
              Hiển thị {{ startIndex }} - {{ endIndex }} trong số
              {{ totalItems }} bản ghi
            </p>
          </b-col>
          <b-col>
            <BPaginationNavCustom
              :number-of-pages="totalPages || 1"
              :current-page="page"
              :total-page="totalPages"
              :linkParams="{}"
              @page-change="fetchData()"
            >
            </BPaginationNavCustom>
          </b-col>
        </b-row>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { VclTable } from 'vue-content-loading';
import { handleError, makeToastSuccess } from '@/utils/common';
import {
  formatDate,
  DATE_FORMAT,
  CLIENT_DATE_TIME,
  DP_CONFIG,
  getSubtractDate,
  formatDBDate,
} from '@/utils/date';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import { checkPermission } from '@/utils/saveDataToLocal';
import { showDeleteAlert } from '@/utils/sweet-alert2';

export default {
  data() {
    return {
      showDeleteAlert,
      DATE_FORMAT,
      CLIENT_DATE_TIME,
      onLoading: false,
      listRewardAffiliates: [],
      fields: [
        {
          key: 'id',
          label: 'ID',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
        {
          key: 'name',
          label: 'Tên chương trình',
          thStyle: { fontWeight: 600, color: '#181c32', width: '25%' },
        },
        {
          key: 'period',
          label: 'Thời gian áp dụng',
          thStyle: { fontWeight: 600, color: '#181c32', width: '20%' },
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
          tdClass: 'text-center',
        },
        {
          key: 'createdAt',
          label: 'Ngày tạo',
          thStyle: { fontWeight: 600, color: '#181c32', width: '15%' },
          formatter: (value) => {
            return value && formatDate(value, CLIENT_DATE_TIME);
          },
        },
        {
          key: 'createdBy',
          label: 'Người tạo',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
        },
        {
          key: 'actions',
          label: 'Hành động',
          thStyle: { width: '10%' },
          tdClass: 'text-center',
        },
      ],
      totalPages: 1,
      totalItems: 0,
      page: 1,
      limit: 10,
      dpConfigs: DP_CONFIG,
      params: {
        startAt: getSubtractDate(30),
        endAt: null,
        keyword: null,
        status: null,
      },
      statusOptions: [
        { value: true, text: 'Hoạt động' },
        { value: false, text: 'Đã ngừng' },
      ],
    };
  },
  methods: {
    checkPermission,
    formatDate,
    async confirmDeleteRewardAffiliate(rewardAffiliate) {
      try {
        const { data } = await ApiService.delete(
          `reward-affiliates/${rewardAffiliate.id}`,
        );
        const foundIndex = this.listRewardAffiliates.findIndex(
          (item) => item.id === rewardAffiliate.id,
        );
        if (foundIndex !== -1) {
          this.listRewardAffiliates.splice(foundIndex, 1);
        }
        makeToastSuccess(data?.message);
      } catch (error) {
        handleError(error);
      }
    },
    getStatusClass(status) {
      if (status) {
        return 'label-light-success';
      }
      return 'label-light-danger';
    },
    getStatusText(status) {
      if (status) {
        return 'Hoạt động';
      }
      return 'Đã ngừng';
    },
    onFilter() {
      this.$route.query.page = 1;
      this.$router.push({
        name: 'list-reward-affiliate',
      });
      this.fetchData();
    },
    setParamFilter() {
      this.page = this.$route.query.page ?? 1;
      return {
        ...this.params,
        startAt: formatDBDate(this.params.startAt),
        endAt: formatDBDate(this.params.endAt),
        page: this.page,
        limit: this.limit,
        status: this.params.status !== null ? +this.params.status : null, //convert boolean to number
      };
    },
    fetchData: async function () {
      try {
        if (this.onLoading) return;
        this.onLoading = true;
        const params = this.setParamFilter();
        const {
          data: { data },
        } = await ApiService.query('reward-affiliates', { params });
        this.listRewardAffiliates = data.result;
        this.totalItems = data.totalItems;
        this.totalPages = data.totalPages;
      } catch (error) {
        handleError(error);
      } finally {
        this.onLoading = false;
      }
    },
    navigateToRewardAffiliate(action, params = {}) {
      this.$router.push({
        name: 'upsert-reward-affiliate',
        query: {
          action,
          ...params,
        },
      });
    },
    openViewDetailPage(item) {
      this.navigateToRewardAffiliate('view', { id: item.id });
    },
    openCreatePage() {
      this.navigateToRewardAffiliate('create');
    },

    openEditPage(item) {
      this.navigateToRewardAffiliate('update', { id: item.id });
    },

    openClonePage(item) {
      this.navigateToRewardAffiliate('create', { cloneId: item.id });
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Chương trình', route: 'reward-affiliates' },
      { title: 'Danh sách chương trình hoa hồng Affiliate' },
    ]);
  },
  created() {
    this.fetchData();
  },
  computed: {
    startIndex() {
      return (this.page - 1) * this.limit + 1;
    },
    endIndex() {
      return this.page * this.limit;
    },
  },
  components: {
    KTCodePreview,
    VclTable,
    BPaginationNavCustom,
  },
};
</script>

<style scoped>
.reward-table {
  background: #f7fafd;
}
.font-weight-bold {
  font-weight: 600 !important;
}
.text-primary {
  color: #007bff !important;
}
.hover {
  cursor: pointer;
}
.hover:hover {
  opacity: 0.7;
  text-decoration: underline;
}
.btn-size {
  width: 90px;
}
.search-col {
  display: flex;
  align-items: center;
}
</style>
